import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { eq, gt, not } from 'ember-truth-helpers';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type LoggerService from 'garaje/services/logger';
import urlBuilder from 'garaje/utils/url-builder';

import DocumentCard from './document-card';
import DocumentListRow from './document-list-row';
import DocumentPreviewModal from './document-preview-modal';
import { type ApprovalStatus, type ProfileDocument } from './document-status';

type ViewMode = 'grid' | 'list';

interface DocumentsPanelSignature {
  Args: {
    visitorProfileId: string;
  };
  Element: HTMLDivElement;
}

interface ProfileDocumentPayload {
  id: string;
  title: string;
  description?: string | null;
  download_url?: string | null;
  status?: ApprovalStatus;
  created_at?: string | null;
  field_values?: Record<string, unknown>;
}

interface ProfileDocumentsResponse {
  data: ProfileDocumentPayload[];
  meta?: { offset: number; limit: number; total: number };
}

const ITEMS_PER_PAGE = 4;

export default class DocumentsPanel extends Component<DocumentsPanelSignature> {
  @service declare ajax: AjaxFetchService;
  @service declare logger: LoggerService;

  @tracked documents: ProfileDocument[] = [];
  @tracked viewMode: ViewMode = 'grid';
  @tracked currentPage = 0;
  @tracked previewIndex = -1;

  constructor(owner: unknown, args: DocumentsPanelSignature['Args']) {
    super(owner, args);
    void this.loadDocuments.perform();
  }

  loadDocuments = restartableTask(async () => {
    try {
      const response = await this.ajax.request<ProfileDocumentsResponse>(
        urlBuilder.v3.visitorProfiles.documents(this.args.visitorProfileId),
      );

      this.documents = response.data.map((doc) => ({
        id: doc.id,
        title: doc.title,
        description: doc.description ?? undefined,
        downloadUrl: doc.download_url ?? undefined,
        approvalStatus: doc.status ?? undefined,
        createdAt: doc.created_at ?? undefined,
        fieldValues: (doc.field_values as Record<string, unknown>) ?? undefined,
      }));
    } catch (e: unknown) {
      // 404 = endpoint not deployed yet — show empty state
      // Other errors should surface (auth failures, server errors)
      const status = (e as { status?: number })?.status;
      if (status === 404) {
        this.documents = [];
      } else {
        this.logger.warn('[DocumentsPanel] Failed to load documents:', e as Error);
        this.documents = [];
      }
    }
  });

  get isLoading(): boolean {
    return this.loadDocuments.isRunning;
  }

  get hasDocuments(): boolean {
    return this.documents.length > 0;
  }

  get totalPages(): number {
    return Math.ceil(this.documents.length / ITEMS_PER_PAGE);
  }

  get paginatedDocuments(): ProfileDocument[] {
    const start = this.currentPage * ITEMS_PER_PAGE;
    return this.documents.slice(start, start + ITEMS_PER_PAGE);
  }

  get canGoBack(): boolean {
    return this.currentPage > 0;
  }

  get canGoForward(): boolean {
    return this.currentPage < this.totalPages - 1;
  }

  get pageLabel(): string {
    const start = this.currentPage * ITEMS_PER_PAGE + 1;
    const end = Math.min(start + ITEMS_PER_PAGE - 1, this.documents.length);
    return `${start}–${end} of ${this.documents.length}`;
  }

  @action
  reloadDocuments(): void {
    this.currentPage = 0;
    void this.loadDocuments.perform();
  }

  @action
  setViewMode(mode: ViewMode): void {
    this.viewMode = mode;
  }

  @action
  goToPreviousPage(): void {
    if (this.canGoBack) this.currentPage--;
  }

  @action
  goToNextPage(): void {
    if (this.canGoForward) this.currentPage++;
  }

  @action
  handleOpenDocument(doc: ProfileDocument): void {
    const index = this.documents.indexOf(doc);
    this.previewIndex = index >= 0 ? index : 0;
  }

  @action
  closePreview(): void {
    this.previewIndex = -1;
  }

  <template>
    <div data-test-documents-panel {{didUpdate this.reloadDocuments @visitorProfileId}} ...attributes>

      {{! Section header: title + view mode toggle }}
      <div class="flex items-center gap-6 mb-6">
        <h2 class="flex-1 text-2xl font-semibold text-carbon-70 m-0 leading-tight" data-test-documents-heading>
          Documents
        </h2>

        {{#if this.hasDocuments}}
          <div class="flex items-center gap-2" data-test-view-mode-toggle>
            {{! List view icon (3 horizontal lines) }}
            <button
              type="button"
              class="p-1 transition-colors"
              aria-label="List view"
              aria-pressed={{eq this.viewMode "list"}}
              data-test-view-mode-list
              {{on "click" (fn this.setViewMode "list")}}
            >
              {{svgJar
                "list-view"
                width="16"
                height="16"
                class=(if (eq this.viewMode "list") "text-red-50" "text-carbon-20")
              }}
            </button>

            {{! Grid view icon (3x3 dots) }}
            <button
              type="button"
              class="p-1 transition-colors"
              aria-label="Grid view"
              aria-pressed={{eq this.viewMode "grid"}}
              data-test-view-mode-grid
              {{on "click" (fn this.setViewMode "grid")}}
            >
              {{svgJar
                "grid-view"
                width="16"
                height="16"
                class=(if (eq this.viewMode "grid") "text-red-50" "text-carbon-20")
              }}
            </button>
          </div>
        {{/if}}
      </div>

      {{! Content }}
      {{#if this.isLoading}}
        <div class="flex items-center justify-center py-12" data-test-documents-loading>
          <LoadingSpinnerPulse />
        </div>
      {{else if this.hasDocuments}}
        {{#if (eq this.viewMode "grid")}}
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-test-documents-grid>
            {{#each this.paginatedDocuments as |doc|}}
              <DocumentCard @document={{doc}} @onOpen={{this.handleOpenDocument}} />
            {{/each}}
          </div>
        {{else}}
          <div class="bg-white rounded-md border border-carbon-10 overflow-hidden" data-test-documents-list>
            {{#each this.paginatedDocuments as |doc|}}
              <DocumentListRow @document={{doc}} @onOpen={{this.handleOpenDocument}} />
            {{/each}}
          </div>
        {{/if}}

        {{! Pagination }}
        {{#if (gt this.totalPages 1)}}
          <nav
            role="navigation"
            aria-label="Pagination"
            class="flex justify-end items-center mt-4"
            data-test-documents-pagination
          >
            <span class="text-sm text-carbon-50" aria-live="polite" aria-atomic="true" data-test-pagination-label>
              {{this.pageLabel}}
            </span>
            <div class="flex items-center ml-4">
              <button
                type="button"
                class="flex items-center p-2 mx-1 {{if this.canGoBack '' 'opacity-30 cursor-not-allowed'}}"
                aria-label="Previous page"
                disabled={{not this.canGoBack}}
                data-test-pagination-prev
                {{on "click" this.goToPreviousPage}}
              >
                {{svgJar "chevron-left-carbon-40" width="9" height="15"}}
              </button>
              <button
                type="button"
                class="flex items-center p-2 mx-1 {{if this.canGoForward '' 'opacity-30 cursor-not-allowed'}}"
                aria-label="Next page"
                disabled={{not this.canGoForward}}
                data-test-pagination-next
                {{on "click" this.goToNextPage}}
              >
                {{svgJar "chevron-right-carbon-40" width="9" height="15"}}
              </button>
            </div>
          </nav>
        {{/if}}
      {{else}}
        <div class="text-carbon-50" data-test-documents-empty-state>
          No documents uploaded for this visitor profile.
        </div>
      {{/if}}

      {{#if (gt this.previewIndex -1)}}
        <DocumentPreviewModal
          @documents={{this.documents}}
          @initialIndex={{this.previewIndex}}
          @onClose={{this.closePreview}}
        />
      {{/if}}
    </div>
  </template>
}
